/**
 * pages/Home.js — Analytics dashboard
 * ======================================
 * The main analytics page showing time-series trends, heatmaps, distribution
 * charts, and a geographic heatmap of user/post activity.
 *
 * Control panel (sticky, top: 70px)
 * -----------------------------------
 * Three controls drive all visualisations.  Changes only mark isDirty=true;
 * the API is NOT called until the user clicks the Reload button.
 *
 *   Month selector  — Select from the last N=12 months (pointer into last_n_months[])
 *   Region selector — TreeSelect from the landmark tree returned by getLandmarks().
 *                     Pin button fills the tree path from the user's login location.
 *                     Limited to one region at a time.
 *   Metric toggle   — Segmented "Users" / "Posts" switch; changes which panels are
 *                     shown (P4.1/Users vs P4.2/Posts, H1.1 vs H1.2).
 *
 * Panels returned by getMonthlyStats()
 * --------------------------------------
 *   P1   — Line chart: Users vs Fact Checkers over the selected month (log-scale y).
 *   P2   — Box plot: Fact-checker response time (minutes) as 5-quantile summary.
 *   P3   — Heatmap: daily SPAM/FAKE/REAL/BLANK counts over the month.
 *          Missing months are synthesised as zero-filled templates by synthesize().
 *   P4.1 — Distribution data for Users perspective (active/passive × real/fake).
 *   P4.2 — Distribution data for Posts perspective (real/fake × active/passive).
 *   B1   — GeoJSON boundary layers rendered on the map (one entry per region).
 *   H1.1 — Heatmap points for Users (lat/lon/weight triples).
 *   H1.2 — Heatmap points for Posts (lat/lon/weight triples).
 *
 * Distribution cards (P4 panels)
 * --------------------------------
 * Four cards, each with three view modes toggled by the "View as" Segmented control:
 *
 *   histogram       — Bar chart of read-receipt % bucketed in 5% increments.
 *   box_plot        — Area chart of the normal distribution approximated from box-plot stats.
 *   percentile_curve — Cumulative area chart: x = percentile rank, y = read-receipt %.
 *
 * Each card renders a vertical reference line at the logged-in user's position
 * within the distribution.  In "Users" mode the raw score is used directly.
 * In "Posts" mode the score is transformed via pitPercentile() (Probability
 * Integral Transform) to map the user's raw score through the Users percentile
 * curve onto the Posts x-axis.
 *
 * isDirty flag
 * -------------
 * Set to true whenever month, region, or metric changes locally.
 * Set to false after a successful refreshRegions() + refreshUserDist() pair.
 * The Reload button is enabled only when isDirty=true.
 *
 * MapCleanup component
 * ----------------------
 * A Leaflet hook component that cancels pending requestAnimationFrame handles
 * on unmount.  Without this, navigating away while the heatmap is rendering
 * throws "Cannot read properties of null (reading 'getSize')" because the
 * RAF callback fires after the Leaflet map has been torn down.
 *
 * API calls
 * ----------
 * apiClient.getLandmarks()                          — POST /landmarks         (public)
 * apiClient.getMonthlyStats(year, month, regions, metric) — POST /session/monthly_stats
 * apiClient.getUserDist(addr, from, to, xpath)      — POST /session/user_dist
 */

import apiClient from '../utils/apiClient';
import { useState, useEffect } from 'react';
import { useUsr } from '../App';
import { EnvironmentOutlined, ReloadOutlined } from '@ant-design/icons';
import { Card, Space, Button, Typography, Flex, Row, Col, TreeSelect, Select, message, Segmented, Spin } from 'antd';
import ReactApexChart from "react-apexcharts";
import { Alert } from 'antd';
import Marquee from 'react-fast-marquee';
import { MapContainer, TileLayer, GeoJSON, Tooltip, useMap } from 'react-leaflet';
import HeatmapLayer from "react-leaflet-heat-layer";
// import geocode from './TN.json';
// const { Title, Text } = Typography;
const { } = Typography

// const { SHOW_PARENT, SHOW_CHILD, SHOW_ALL } = TreeSelect;
const { SHOW_PARENT } = TreeSelect;

/**
 * MapCleanup — Leaflet utility component (renders nothing).
 *
 * Problem: react-leaflet-heat-layer and Leaflet's Canvas renderer both queue
 * requestAnimationFrame callbacks.  When the user navigates away quickly the
 * map DOM node is removed but the RAF fires after unmount, crashing with
 * "Cannot read properties of null (reading 'getSize')".
 *
 * Fix: on unmount, cancel all pending RAF handles found on:
 *   layer._frame       — HeatLayer animation frame
 *   map._renderer._frame — Leaflet Canvas renderer redraw frame
 *   map._animFrame     — Leaflet map-level animation frame
 */
const MapCleanup = () => {
    const map = useMap();
    useEffect(() => {
        return () => {
            // Cancel any pending animation frames on each HeatLayer
            map.eachLayer(layer => {
                if (layer._frame) {
                    cancelAnimationFrame(layer._frame);
                    layer._frame = null;
                    layer._map = null;
                }
            });
            // Cancel the Leaflet Canvas renderer's pending redraw (source of 'getSize' crash)
            if (map._renderer?._frame) {
                cancelAnimationFrame(map._renderer._frame);
                map._renderer._frame = null;
            }
            // Cancel the map-level animation frame
            if (map._animFrame) {
                cancelAnimationFrame(map._animFrame);
                map._animFrame = null;
            }
        };
    }, [map]);
    return null;
};

// Number of months to show in the month selector
const N = 12

// Short month names for the Select option labels
const shortMonthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

/**
 * synthesize — builds an empty heatmap data structure for a given month/year.
 *
 * Used as a fallback when the backend returns no P3 data (e.g. a month with
 * no submissions yet).  Produces 4 series (SPAM/FAKE/REAL/BLANK) each with:
 *   - `date` days of y=0  (valid days, shown as empty cells)
 *   - `31 - date` padding days of y=−1 (invalid cells, rendered white)
 *
 * This keeps the ApexCharts heatmap grid a fixed 4×31 shape regardless of
 * whether the month has 28, 29, 30, or 31 days.
 *
 * @param {number} month — 1-based month number.
 * @param {number} year  — 4-digit year.
 * @returns {object}     — { total: 0, results: [...] } matching getMonthlyStats shape.
 */
function synthesize(month, year) {

    const date = new Date(year, month, 0).getDate()

    const template = {
        total: 0,
        results: [
            {
                name: 'SPAM',
                data: Array.from({ length: date }, (_, i) => ({
                    x: `${String(i + 1).padStart(2, '0')}/${String(month).padStart(2, '0')}`,
                    y: 0,
                })).concat(Array.from({ length: 31 - date }, (_, i) => ({
                    x: `${String(date + i + 1).padStart(2, '0')}/${String(month).padStart(2, '0')}`,
                    y: -1,
                })))
            },
            {
                name: 'FAKE',
                data: Array.from({ length: date }, (_, i) => ({
                    x: `${String(i + 1).padStart(2, '0')}/${String(month).padStart(2, '0')}`,
                    y: 0,
                })).concat(Array.from({ length: 31 - date }, (_, i) => ({
                    x: `${String(date + i + 1).padStart(2, '00')}/${String(month).padStart(2, '00')}`,
                    y: -1,
                })))
            },
            {
                name: 'REAL',
                data: Array.from({ length: date }, (_, i) => ({
                    x: `${String(i + 1).padStart(2, '0')}/${String(month).padStart(2, '0')}`,
                    y: 0,
                })).concat(Array.from({ length: 31 - date }, (_, i) => ({
                    x: `${String(date + i + 1).padStart(2, '0')}/${String(month).padStart(2, '0')}`,
                    y: -1,
                })))
            },
            {
                name: 'BLANK',
                data: Array.from({ length: date }, (_, i) => ({
                    x: `${String(i + 1).padStart(2, '0')}/${String(month).padStart(2, '0')}`,
                    y: 0,
                })).concat(Array.from({ length: 31 - date }, (_, i) => ({
                    x: `${String(date + i + 1).padStart(2, '0')}/${String(month).padStart(2, '0')}`,
                    y: -1,
                })))
            }
        ]
    }
    return template
}

/**
 * getLastNMonths — returns an array of { year, month } objects for the last
 * `last_n_months` months, oldest first (index 0 = oldest, index N-1 = current).
 *
 * Used to populate the month Select options and to derive API date range params.
 * The array is reversed at the end so the selector shows oldest→newest top-down.
 *
 * @param {number} last_n_months — number of months to generate (e.g. 12).
 * @returns {{ year: number, month: number }[]}
 */
function getLastNMonths(last_n_months) {
    const result = [];
    const now = new Date();

    for (let i = 0; i < last_n_months; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        result.push({
            year: date.getFullYear(),
            month: date.getMonth() + 1
        });
    }

    return result.reverse();
}

export const Home = () => {

    const { usr } = useUsr();

    // "Users" or "Posts" — controls which P4 panel and heatmap layer is shown
    const [metric, setMetric] = useState(() => localStorage.getItem('fnma_home_metric') || 'Users');

    // Landmark tree data from /landmarks — used to populate the TreeSelect options
    const [landmarks, setLandmarks] = useState([]);

    // Currently selected region path(s), e.g. ["Tamil Nadu/Coimbatore/Kinathukadavu"]
    // Limited to one entry at a time (enforced in editRegion).
    const [regions, setRegions] = useState(() => {
        try { return JSON.parse(localStorage.getItem('fnma_home_regions')) || []; }
        catch { return []; }
    });

    // Fixed list of the last N months — generated once on mount, never changes
    const [last_n_months] = useState(getLastNMonths(N));

    // Index into last_n_months[]; N-1 = current month (most recent)
    const [pointer, setPointer] = useState(() => {
        const v = localStorage.getItem('fnma_home_pointer');
        return v !== null ? parseInt(v, 10) : N - 1;
    });

    // Mirrors pointer but only advances after a successful refreshRegions().
    // P1/P2 category generation uses this so old data stays visible until Reload.
    const [fetchedPointer, setFetchedPointer] = useState(N - 1);

    // Backend data freshness timestamp returned with each getMonthlyStats response
    const [ts, setTS] = useState("")

    // --- Panel state: each variable holds the data for one dashboard panel ---

    // P1 — line chart: user/fact-checker registration trend over the selected month
    const [P1, setP1] = useState({})

    // P2 — box plot: fact-checker response time distribution (minutes)
    const [P2, setP2] = useState({})

    // P3 — heatmap: daily SPAM/FAKE/REAL/BLANK counts as a 4×31 grid
    const [P3, setP3] = useState({})

    // P4.1 — engagement distribution from the Users perspective
    //         keys: "active vs real", "active vs fake", "passive vs real", "passive vs fake"
    const [P4_1, setP4_1] = useState({})

    // P4.2 — engagement distribution from the Posts perspective
    //         keys: "real vs active", "fake vs active", "real vs passive", "fake vs passive"
    const [P4_2, setP4_2] = useState({})

    // Which distribution view mode is active: 'histogram' | 'box_plot' | 'percentile_curve'
    const [chartType, setChartType] = useState(() => localStorage.getItem('fnma_home_charttype') || 'histogram')

    // B1 — GeoJSON boundary objects keyed by region property name
    const [B1, setB1] = useState({})

    // H1.1 — heatmap points for Users  [ [lat, lon, weight], ... ]
    const [H1_1, setH1_1] = useState([])

    // H1.2 — heatmap points for Posts  [ [lat, lon, weight], ... ]
    const [H1_2, setH1_2] = useState([])

    // Logged-in user's personal scores within the P4 distributions.
    // null when not logged in or when fetch fails.
    const [userDist, setUserDist] = useState(null)

    // isDirty tracks whether local controls have drifted from the last fetched data.
    // false = visualisations match current inputs (Reload disabled)
    // true  = an input changed, data is stale (Reload enabled)
    const [isDirty, setIsDirty] = useState(false)

    // true while any dashboard fetch is in-flight — panels show skeletons
    const [loading, setLoading] = useState(false)

    const [messageApi, messageHolder] = message.useMessage();

    // On mount: load the landmark tree and fetch the initial dashboard data
    useEffect(() => {

        apiClient.getLandmarks()
            .then(response => { setLandmarks(response.data) })
            .catch(error => { console.error("Error sending data: ", error); });

        setLoading(true);
        Promise.all([refreshRegions(), refreshUserDist()])
            .finally(() => setLoading(false));

    }, []);

    /**
     * editRegion — TreeSelect onChange handler.
     * Enforces the one-region-at-a-time limit and marks the dashboard as dirty.
     *
     * @param {string[]} newValue — array of selected tree paths.
     */
    const editRegion = newValue => {
        if (newValue.length <= 1) {
            setRegions(newValue);
            localStorage.setItem('fnma_home_regions', JSON.stringify(newValue));
            setIsDirty(true);
        } else {
            messageApi.info('One region at a time.')
        }
    };

    /**
     * refreshUserDist — fetches the logged-in user's personal read-receipt scores
     * for the currently selected month and region.
     *
     * The returned data has shape:
     *   { users: { "active vs real": { score: number }, ... } }
     *
     * The score is a read-receipt percentage (0–100) used to draw a reference
     * line on the P4 distribution charts.  Returns a resolved Promise (no-op)
     * when the user is not logged in so Promise.all() in the Reload handler
     * always resolves cleanly.
     */
    const refreshUserDist = () => {
        if (!usr.token || !usr.addr) { setUserDist(null); return Promise.resolve(); }
        const { year, month } = last_n_months[pointer];
        const from_dt = `${year}-${String(month).padStart(2, '0')}-01`;
        // First day of the *next* month as the exclusive upper bound
        const nd = new Date(year, month, 1);
        const to_dt = `${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, '0')}-01`;
        const xpath = regions.length ? regions[0] : '';
        return apiClient.getUserDist(usr.addr, from_dt, to_dt, xpath)
            .then(r => setUserDist(r.data))
            .catch(() => setUserDist(null));
    };

    /**
     * refreshRegions — calls getMonthlyStats and distributes the response into
     * the individual panel state variables (P1…H1.2).
     *
     * Panel assignment logic:
     *   P1/P2 — use presence + shape check; fall back to empty object.
     *   P3    — if missing or malformed, use synthesize() to generate an empty template.
     *   P4.1/P4.2 — merge with a safe default targetMap so components never receive
     *               partially-shaped data when the backend omits inner keys.
     *   B1/H1.1/H1.2 — set directly from response.
     *
     * Note: P2 guard intentionally checks P1's presence (the original code mirrors
     * the P1 guard pattern — both panels are always returned together).
     */
    const refreshRegions = () => {
        const snapshotPointer = pointer;

        return apiClient.getMonthlyStats(last_n_months[pointer].year, last_n_months[pointer].month, regions, metric)
            .then(response => {

                // Advance fetchedPointer now that data for this month is confirmed
                setFetchedPointer(snapshotPointer);

                var targetMap = {}

                if (response.data.hasOwnProperty('ts')) {
                    setTS(response.data.ts)
                }

                if (response.data.hasOwnProperty('P1')) {
                    const panel = response.data.P1
                    panel.hasOwnProperty('total') && panel.hasOwnProperty('results')
                        ?
                        setP1(response.data.P1)
                        :
                        setP1({
                            total: 0,
                            results: []
                        })
                }

                if (response.data.hasOwnProperty('P2')) {
                    const panel = response.data.P1
                    panel.hasOwnProperty('total') && panel.hasOwnProperty('results')
                        ?
                        setP2(response.data.P2)
                        :
                        setP2({
                            total: 0,
                            results: []
                        })
                }

                if (response.data.hasOwnProperty('P3')) {
                    const panel = response.data.P3
                    panel.hasOwnProperty('total') && panel.hasOwnProperty('results')
                        ?
                        setP3(response.data.P3)
                        :
                        setP3(synthesize(last_n_months[pointer].month, last_n_months[pointer].year))
                }

                if (response.data.hasOwnProperty('P4.1')) {
                    // Safe default ensures all four distribution keys are always present
                    // even if the backend omits some (e.g. no data for a region/month)
                    targetMap = {
                        total: 0,
                        total_users_vs_real_claims: { users: 0, claims: 0 },
                        total_users_vs_fake_claims: { users: 0, claims: 0 },
                        results: {
                            'active vs real': {
                                'stats': null,
                                'norm_dist': []
                            },
                            'active vs fake': {
                                'stats': null,
                                'norm_dist': []
                            },
                            'passive vs real': {
                                'stats': null,
                                'norm_dist': []
                            },
                            'passive vs fake': {
                                'stats': null,
                                'norm_dist': []
                            }
                        }
                    }
                    const sourceMap = response.data['P4.1']

                    // Merge: only overwrite keys that exist in both target and source
                    for (const outerKey in targetMap) {
                        if (sourceMap.hasOwnProperty(outerKey)) {
                            for (const innerKey in targetMap[outerKey]) {
                                if (sourceMap[outerKey].hasOwnProperty(innerKey)) {
                                    targetMap[outerKey][innerKey] = sourceMap[outerKey][innerKey];
                                }
                            }
                        }
                    }
                    setP4_1(targetMap)
                }

                if (response.data.hasOwnProperty('P4.2')) {
                    // Safe default for the Posts-perspective distribution keys
                    targetMap = {
                        total: 0,
                        real_claims_vs_total_users: { claims: 0, users: 0 },
                        fake_claims_vs_total_users: { claims: 0, users: 0 },
                        results: {
                            'real vs active': {
                                'stats': null,
                                'norm_dist': []
                            },
                            'fake vs active': {
                                'stats': null,
                                'norm_dist': []
                            },
                            'real vs passive': {
                                'stats': null,
                                'norm_dist': []
                            },
                            'fake vs passive': {
                                'stats': null,
                                'norm_dist': []
                            }
                        }
                    }

                    const sourceMap = response.data['P4.2']

                    for (const outerKey in targetMap) {
                        if (sourceMap.hasOwnProperty(outerKey)) {
                            for (const innerKey in targetMap[outerKey]) {
                                if (sourceMap[outerKey].hasOwnProperty(innerKey)) {
                                    targetMap[outerKey][innerKey] = sourceMap[outerKey][innerKey];
                                }
                            }
                        }
                    }
                    setP4_2(targetMap)

                }

                if (response.data.hasOwnProperty('B1')) {
                    setB1(response.data.B1)
                }

                if (response.data.hasOwnProperty('H1.1')) {
                    setH1_1(response.data['H1.1'])
                }

                if (response.data.hasOwnProperty('H1.2')) {
                    setH1_2(response.data['H1.2'])
                }

            })
            .catch(error => {
                console.error("Error sending data: ", error);
            });

    };

    /**
     * renderDistChart — renders one P4 distribution card's inner chart.
     *
     * @param {object|null} seriesData  — the distribution data for this card
     *                                    (e.g. P4_1.results['active vs real'])
     *                                    Shape: { histogram, percentile_curve, box_plot, meta }
     * @param {string}      color       — ApexCharts series colour hex.
     * @param {boolean}     reverse     — if true, reverses the box_plot x-axis.
     *                                    Used for FAKE cards (higher score = fewer reads).
     * @param {string}      entity      — label for the subject being measured (e.g. 'users').
     * @param {string}      denominator — label for what it's measured against (e.g. 'posts').
     * @param {number|null} userScore   — logged-in user's score (0–100), or null if
     *                                    not available.  Drawn as a blue reference line.
     * @returns {JSX.Element}
     *
     * Chart modes
     * -----------
     * histogram       — Bar chart, x = read-receipt % bucket (0–100 in 5% steps),
     *                   y = count.  Q1/Median/Q3 and userScore drawn as x-axis annotations.
     *
     * box_plot        — Area chart rendered from a normal distribution approximation.
     *                   x = read-receipt % (0–100), y = probability density.
     *                   σ (standard deviation) is estimated as IQR/1.35 or range/6.
     *                   Reversed for FAKE cards (array reversal, not xaxis.reversed —
     *                   ApexCharts category axes don't support reversed reliably).
     *
     * percentile_curve — Area chart, x = percentile rank (5% steps from backend),
     *                    y = read-receipt % at that rank.  userScore drawn as y-axis
     *                    annotation (horizontal line) for the cumulative view.
     *
     * Forward quantile lookup for Posts mode
     * -----------------------------------------------------
     * When metric='Posts', the user's raw score (from userDist.users[key].score)
     * is treated as a percentile rank and looked up against the Posts
     * distribution's own percentile_curve, so the marker always lands within
     * that chart's observed range rather than being inverted through a
     * differently-shaped distribution. This is done by curveValueAtRank()
     * inside the map() that renders the four distribution cards.
     */
    const renderDistChart = (seriesData, color, reverse = false, entity = 'entities', denominator = 'total', userScore = null) => {
        // Shared empty baseline — 21 zero points matching the chart's direction.
        // FAKE charts (reverse=true) run 100%→0% to mirror the backend-reversed data.
        // Used by every branch when data is absent so card size & x-axis stay fixed.
        const EMPTY = Array.from({ length: 21 }, (_, i) => ({ x: `${reverse ? 100 - i * 5 : i * 5}%`, y: 0 }));

        // The backend already reverses histogram and percentile_curve for FAKE
        // distributions before sending them.  Do NOT reverse again here.
        // The box_plot normal curve is generated client-side and IS reversed below.
        const histogram = seriesData?.histogram || EMPTY;
        const percentile_curve = seriesData?.percentile_curve || EMPTY;
        const bp = seriesData?.box_plot || null;
        const meta = seriesData?.meta || null;

        // Format absolute counts as K / M for compact display
        const fmtCount = n => {
            if (!n) return '0';
            if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
            if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
            return `${n}`;
        };

        const gradientFill = {
            type: 'gradient',
            gradient: { shadeIntensity: 1, opacityFrom: 0.55, opacityTo: 0.05, stops: [0, 90, 100] }
        };

        // Shared tooltip helpers — build a consistent styled tooltip across all chart types
        const tipStyle = [
            'padding:10px 12px', 'font-size:12px', 'line-height:1.8',
            'background:#fff', 'border:1px solid #e8e8e8', 'border-radius:8px',
            'box-shadow:0 2px 8px rgba(0,0,0,0.12)', 'min-width:180px'
        ].join(';');
        const row = (label, val, bold = false) =>
            `<tr>
                <td style="color:#888;padding-right:16px;">${label}</td>
                <td style="text-align:right;${bold ? 'font-weight:700;' : 'font-weight:500;'}">${val}</td>
            </tr>`;
        // quantileTable — renders the 5-quantile summary table inside each tooltip
        const quantileTable = (b) => b ? `
            <div style="margin-top:6px;font-weight:600;border-top:1px solid #f0f0f0;padding-top:5px;">5-Quantile Summary</div>
            ${meta && meta.n ? `<div style="font-size:11px;color:#666;margin-bottom:3px;">${fmtCount(meta.n)} ${entity} · ${fmtCount(meta.total)} ${denominator}</div>` : ''}
            <table style="width:100%;border-collapse:collapse;">
                <tr><td colspan="2" style="border-top:1px solid #e8e8e8;padding:2px 0;"></td></tr>
                ${row('n', fmtCount(b.count))}
                ${row('μ Mean', b.mean + '%', true)}
                ${row('σ Std Dev', (b.std ?? '—') + (b.std != null ? '%' : ''), true)}
                <tr><td colspan="2" style="border-top:1px solid #e8e8e8;padding:2px 0;"></td></tr>
                ${row('Min', b.min + '%')}
                ${row('Q1 (25th)', b.q1 + '%')}
                ${row('Median (50th)', b.median + '%')}
                ${row('Q3 (75th)', b.q3 + '%')}
                ${row('Max', b.max + '%')}
            </table>` : '';

        // userLine — blue dashed annotation showing where the logged-in user sits.
        // isYaxis=true renders a horizontal line (for the cumulative percentile_curve chart).
        // offsetX nudges the label box away from the chart edge when the annotation lands
        // exactly at 0% or 100%, where ApexCharts clips or omits the label.
        const userLine = (val, isYaxis = false, offsetX = 0) => userScore != null ? [{
            [isYaxis ? 'y' : 'x']: val,
            strokeDashArray: 3,
            borderColor: '#1890ff',
            borderWidth: 2,
            label: {
                text: `You ${userScore.toFixed(1)}%`,
                ...(offsetX !== 0 && { offsetX }),
                style: { fontSize: '10px', color: '#fff', background: '#1890ff' }
            }
        }] : [];

        // edgeNudge — returns offsetX to prevent ApexCharts clipping annotation labels at
        // the chart boundary.  Left-edge category → nudge right (+40); right-edge → left (−40).
        // For reversed FAKE charts the left/right edges are swapped.
        const edgeNudge = catStr => {
            if (catStr === (reverse ? '100%' : '0%')) return 40;
            if (catStr === (reverse ? '0%' : '100%')) return -40;
            return 0;
        };

        // qLine — consistent Q1 / Median / Q3 annotation style across all chart types
        const qLine = (x, label, strong = false) => ({
            x,
            strokeDashArray: strong ? 0 : 4,
            borderColor: strong ? '#555' : '#bbb',
            borderWidth: strong ? 2 : 1,
            label: {
                text: label,
                style: {
                    fontSize: '10px',
                    color: strong ? '#fff' : '#555',
                    background: strong ? '#555' : '#f0f0f0'
                }
            }
        });

        if (chartType === 'histogram') {
            // Snap a float % to the nearest 5% category label.
            // The histogram x-labels come directly from the data ('0%','5%'…'100%' for
            // REAL, '100%','95%'…'0%' for FAKE — already reversed by the backend).
            // Annotation x values are the raw bp stat strings which match those labels.
            const snap = v => `${Math.round(v / 5) * 5}%`;
            return (
                <ReactApexChart
                    key={chartType + color + reverse}
                    options={{
                        chart: { type: 'bar', toolbar: { show: false }, animations: { enabled: false } },
                        xaxis: {
                            title: { text: reverse ? 'Read Receipt % (100 → 0)' : 'Read Receipt %' },
                            crosshairs: { show: true, stroke: { color: '#aaa', dashArray: 3 } }
                        },
                        yaxis: { title: { text: 'Count' } },
                        colors: [color],
                        dataLabels: { enabled: false },
                        plotOptions: { bar: { borderRadius: 3, columnWidth: '85%' } },
                        grid: { strokeDashArray: 3 },
                        annotations: {
                            xaxis: [
                                ...(bp ? [
                                    qLine(snap(bp.q1), `Q1 ${bp.q1}%`),
                                    qLine(snap(bp.median), `Med ${bp.median}%`, true),
                                    qLine(snap(bp.q3), `Q3 ${bp.q3}%`)
                                ] : []),
                                ...userLine(snap(userScore), false, edgeNudge(snap(userScore)))
                            ]
                        },
                        tooltip: {
                            custom: ({ series, seriesIndex, dataPointIndex }) => {
                                const count = series[seriesIndex][dataPointIndex];
                                if (!count) return '';
                                const pt = seriesData?.histogram?.[dataPointIndex];
                                // For reversed FAKE arrays (backend-reversed), index 0 = 100% bucket.
                                const lo = reverse ? 100 - dataPointIndex * 5 : dataPointIndex * 5;
                                const range = lo >= 100 ? '= 100%' : `${lo}% – ${lo + 5}%`;
                                const popLine = meta?.n
                                    ? `<div style="color:#888;font-size:11px;">${fmtCount(count)} of ${fmtCount(meta.n)} ${entity} &nbsp;·&nbsp; ${pt?.pct ?? 0}% of population</div>`
                                    : '';
                                return `<div style="${tipStyle}">
                                    <div style="font-weight:600;margin-bottom:4px;">Read Receipt ${range}</div>
                                    <div>Count: <b>${fmtCount(count)}</b></div>
                                    ${popLine}
                                    ${quantileTable(bp)}
                                </div>`;
                            }
                        }
                    }}
                    series={[{ name: 'Count', data: histogram }]}
                    type="bar"
                    height={230}
                />
            );
        }

        if (chartType === 'box_plot') {
            // Normal distribution approximation from box-plot stats.
            // σ estimated as max(IQR/1.35, range/6, 0.5) — the three fallbacks prevent
            // division-by-zero when the distribution is very narrow or has few samples.
            // When no stats, render 101 flat-zero points so the card keeps its fixed size & x-axis
            let normalPoints = bp
                ? Array.from({ length: 101 }, (_, x) => ({
                    x: `${x}%`,
                    y: parseFloat(
                        (Math.exp(-0.5 * ((x - bp.mean) / Math.max((bp.q3 - bp.q1) / 1.35, (bp.max - bp.min) / 6, 0.5)) ** 2)
                            / (Math.max((bp.q3 - bp.q1) / 1.35, (bp.max - bp.min) / 6, 0.5) * Math.sqrt(2 * Math.PI)) * 100).toFixed(4)
                    )
                }))
                : Array.from({ length: 101 }, (_, x) => ({ x: `${x}%`, y: 0 }));
            // Reverse data array for FAKE cards (100%→0%) — xaxis.reversed is unreliable on category axes
            if (reverse) normalPoints = [...normalPoints].reverse();
            // Annotation x values use the raw bp stat strings which directly match the
            // category labels in the reversed array (e.g. '20%' lands at the correct
            // screen position whether or not the array is reversed).
            const bpX = v => `${Math.round(v)}%`;
            return (
                <ReactApexChart
                    key={chartType + color + reverse}
                    options={{
                        chart: { type: 'area', toolbar: { show: false }, animations: { enabled: false } },
                        xaxis: {
                            title: { text: reverse ? 'Read Receipt % (100 → 0)' : 'Read Receipt %' }, tickAmount: 11,
                            crosshairs: { show: true, stroke: { color: '#aaa', dashArray: 3 } }
                        },
                        yaxis: { title: { text: 'Density' }, labels: { formatter: v => v.toFixed(2) } },
                        stroke: { curve: 'smooth', width: 2 },
                        fill: gradientFill,
                        colors: [color],
                        dataLabels: { enabled: false },
                        markers: { size: 0 },
                        grid: { strokeDashArray: 3 },
                        annotations: {
                            xaxis: [
                                ...(bp ? [
                                    qLine(bpX(bp.q1), `Q1 ${bp.q1}%`),
                                    qLine(bpX(bp.median), `Med ${bp.median}%`, true),
                                    qLine(bpX(bp.q3), `Q3 ${bp.q3}%`),
                                    {
                                        x: bpX(bp.mean), x2: bpX(bp.mean),
                                        strokeDashArray: 0, borderColor: '#999', borderWidth: 1,
                                        label: { text: `μ ${bp.mean}% · σ ${bp.std ?? '?'}%`, offsetY: 30, style: { fontSize: '10px', color: '#fff', background: '#999' } }
                                    },
                                    // IQR shading: Q1→Q3 band — raw values match the category labels
                                    { x: bpX(bp.q1), x2: bpX(bp.q3), fillColor: color + '18' }
                                ] : []),
                                ...userLine(bpX(userScore), false, edgeNudge(bpX(userScore)))
                            ]
                        },
                        tooltip: {
                            custom: ({ series, seriesIndex, dataPointIndex }) => {
                                const density = series[seriesIndex][dataPointIndex];
                                // For reversed (FAKE) charts, dataPointIndex 0 = 100%, so invert
                                const xPct = reverse ? (100 - dataPointIndex) : dataPointIndex;
                                return `<div style="${tipStyle}">
                                    <div style="font-weight:600;border-bottom:1px solid #f0f0f0;padding-bottom:4px;margin-bottom:4px;">Normal Distribution</div>
                                    <div style="font-size:11px;color:#666;margin-bottom:2px;">Density at ${xPct}%: ${density.toFixed(4)}</div>
                                    ${quantileTable(bp)}
                                </div>`;
                            }
                        }
                    }}
                    series={[{ name: 'Density', data: normalPoints }]}
                    type="area"
                    height={230}
                />
            );
        }

        // percentile_curve — cumulative area chart.
        // x = percentile rank (5% steps: 0%, 5%, … 100%) from the backend.
        // y = read-receipt % at that rank.
        // For FAKE cards the array is reversed so rank reads 100%→0%; Q1/Med/Q3
        // x-annotation positions are mirrored (25%↔75%) to stay on the correct bucket.
        // userScore drawn as a horizontal y-axis annotation (where the user sits on the curve).
        return (
            <ReactApexChart
                key={chartType + color + reverse}
                options={{
                    chart: { type: 'area', toolbar: { show: false }, animations: { enabled: false } },
                    xaxis: {
                        title: { text: reverse ? 'Percentile Rank (100 → 0)' : 'Percentile Rank' },
                        crosshairs: { show: true, stroke: { color: '#aaa', dashArray: 3 } }
                    },
                    yaxis: { title: { text: 'Read Receipt %' }, min: 0, max: 100 },
                    stroke: { curve: 'smooth', width: 2 },
                    fill: gradientFill,
                    colors: [color],
                    dataLabels: { enabled: false },
                    markers: { size: 4, hover: { size: 6 } },
                    grid: { strokeDashArray: 3 },
                    annotations: {
                        xaxis: [
                            ...(bp ? [
                                // Percentile rank labels ('25%' etc.) are present in the
                                // backend-reversed data as category strings — no mirroring needed.
                                qLine('25%', `Q1 ${bp.q1}%`),
                                qLine('50%', `Med ${bp.median}%`, true),
                                qLine('75%', `Q3 ${bp.q3}%`)
                            ] : []),
                        ],
                        // Horizontal line: "You are here" on the cumulative curve (y-axis, no mirroring needed)
                        yaxis: userLine(Math.max(0.5, Math.min(99.5, userScore)), true)
                    },
                    tooltip: {
                        custom: ({ series, seriesIndex, dataPointIndex }) => {
                            const y = series[seriesIndex][dataPointIndex];
                            if (y === null || y === undefined) return '';
                            // Backend-reversed data: dataPointIndex maps directly to the
                            // correct element regardless of REAL/FAKE direction.
                            const pt = seriesData?.percentile_curve?.[dataPointIndex];
                            // pt.x is the original percentile label (preserved through reversal)
                            const pctLabel = pt?.x ?? `${dataPointIndex * 5}%`;
                            const rankCount = pt?.rank ?? null;
                            const rankLine = (rankCount !== null && meta?.n)
                                ? `<div style="color:#888;font-size:11px;">${fmtCount(rankCount)} of ${fmtCount(meta.n)} ${entity}</div>`
                                : '';
                            let band = '';
                            if (bp) {
                                if (y < bp.q1) band = `Below Q1 (&lt; ${bp.q1}%)`;
                                else if (y < bp.median) band = `Q1 → Median (${bp.q1}% – ${bp.median}%)`;
                                else if (y < bp.q3) band = `Median → Q3 (${bp.median}% – ${bp.q3}%)`;
                                else band = `Above Q3 (&gt; ${bp.q3}%)`;
                            }
                            return `<div style="${tipStyle}">
                                <div style="font-weight:600;margin-bottom:4px;">Percentile ${pctLabel}</div>
                                <div>Read Receipt: <b>${y.toFixed(1)}%</b></div>
                                ${rankLine}
                                ${band ? `<div style="color:#888;font-size:11px;margin-top:2px;">${band}</div>` : ''}
                                ${quantileTable(bp)}
                            </div>`;
                        }
                    }
                }}
                series={[{ name: 'Read Receipt %', data: percentile_curve }]}
                type="area"
                height={230}
            />
        );
    };

    return (

        <div style={{
            marginLeft: 50,
            marginTop: 30,
            marginRight: 50,
            marginBottom: 30,
            alignItems: "center",
            justifyContent: "center",
        }}>
            {/* ----------------------------------------------------------------
                Sticky control panel — sits just below the header (top: 70px).
                All three inputs mark isDirty=true but do NOT fire an API call.
                The Reload button fires refreshRegions() + refreshUserDist()
                together and resets isDirty to false on completion.
            ---------------------------------------------------------------- */}
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '10px',
                position: 'sticky',
                top: '70px',
                padding: '1rem',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, rgba(128, 222, 234, 0.32), rgba(224, 247, 250, 0.48))', // bright light teal
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)', // For Safari
                boxShadow: '0 4px 16px rgba(0, 188, 212, 0.18), 0 1px 3px rgba(0,0,0,0.06)',
                border: '1px solid rgba(128, 222, 234, 0.45)',
                color: '#000',
                zIndex: 100,
            }} >
                {messageHolder}
                <Row gutter={[12, 12]} align="middle" style={{ width: '100%' }}>

                    {/* Month selector — updates pointer (index into last_n_months[]) */}
                    <Col xs={24} md={6} style={{ display: 'flex', justifyContent: 'center' }}>
                        <Select
                            value={pointer}
                            onChange={v => { setPointer(v); localStorage.setItem('fnma_home_pointer', v); setIsDirty(true); }}
                            style={{ minWidth: 120 }}
                            options={last_n_months.map((m, i) => ({
                                value: i,
                                label: `${shortMonthNames[m.month - 1]}-${m.year}`,
                            }))}
                            popupMatchSelectWidth={false}
                        />
                    </Col>

                    {/* Region selector — Pin button fills from login location;
                        TreeSelect limited to one selection; Reload fires the API */}
                    <Col xs={24} md={12} style={{ display: 'flex', justifyContent: 'center' }}>
                        <Space.Compact style={{ width: '100%', maxWidth: 500 }}>
                            {/* Pin button — auto-fills the tree path from usr.taluk (login location) */}
                            <Button
                                icon={<EnvironmentOutlined />}
                                title="Fill from login location"
                                disabled={!usr.taluk}
                                onClick={() => {
                                    const xpath = usr.taluk
                                        ? `${usr.state}/${usr.district}/${usr.taluk}`
                                        : null;
                                    if (xpath) { setRegions([xpath]); localStorage.setItem('fnma_home_regions', JSON.stringify([xpath])); setIsDirty(true); }
                                }}
                            />
                            <TreeSelect
                                style={{ width: '100%' }}
                                value={regions}
                                styles={{
                                    popup: { root: { maxHeight: 500, overflow: 'auto' } },
                                }}
                                treeData={landmarks}
                                placeholder="Please select"
                                showCheckedStrategy={SHOW_PARENT}
                                treeDefaultExpandedKeys={[]}
                                treeCheckable
                                multiple
                                onChange={editRegion}
                                listHeight={256}
                                popupMatchSelectWidth={false}
                            />
                            {/* Reload button — enabled only when isDirty; fires both refreshes */}
                            <Button
                                icon={<ReloadOutlined />}
                                disabled={!isDirty}
                                onClick={() => {
                                    setLoading(true);
                                    Promise.all([refreshRegions(), refreshUserDist()])
                                        .finally(() => { setIsDirty(false); setLoading(false); });
                                }}
                            />
                        </Space.Compact>
                    </Col>

                    {/* Users / Posts metric toggle — switches P4 panel and map heatmap layer */}
                    <Col xs={24} md={6} style={{ display: 'flex', justifyContent: 'center' }}>
                        <Segmented
                            options={['Users', 'Posts']}
                            value={metric}
                            onChange={v => { setMetric(v); localStorage.setItem('fnma_home_metric', v); }}
                            size="middle"
                        />
                    </Col>

                </Row>

            </div>

            <Flex
                vertical={true}
                gap={10}
                justify='center'>

                {/* ----------------------------------------------------------------
                    P1 — Line chart: Users vs Fact Checkers over the selected month.
                    Solid lines = cumulative registered (ts); dashed = cumulative
                    active this month (usr_ts). Gap between pairs = dormant users.
                    Y-axis is logarithmic to handle large disparities in counts.
                ---------------------------------------------------------------- */}
                <Card title="[ Trend Plot ] - Users vs Fact Checkers (Registered vs Active)"
                    variant="outlined"
                    style={{
                        border: '1px solid #dadce0'
                    }}
                >

                    <div style={{ minHeight: 350 }}>
                    {loading
                        ? <div style={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="large" /></div>
                        : P1 && P1.results && (() => {
                            const { year, month } = last_n_months[fetchedPointer];
                            const daysInMonth = new Date(year, month, 0).getDate();
                            // All dd/MM category labels for the selected month
                            const cats = Array.from({ length: daysInMonth }, (_, i) =>
                                `${String(i + 1).padStart(2, '0')}/${String(month).padStart(2, '0')}`
                            );
                            // Transform each series: map backend timestamps to dd/MM keys,
                            // fill every day of the month (null for days with no data)
                            const transformedSeries = P1.results.map((s, i) => {
                                const lookup = {};
                                s.data.forEach(pt => {
                                    const d = new Date(pt.x);
                                    const k = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
                                    lookup[k] = pt.y;
                                });
                                return {
                                    ...s,
                                    type: i < 2 ? 'area' : 'line',
                                    data: cats.map(c => ({ x: c, y: lookup[c] ?? null }))
                                };
                            });
                            return (
                                <ReactApexChart
                                    options={{
                                        chart: {
                                            type: "line",
                                            zoom: { enabled: false },
                                            toolbar: { show: false },
                                            animations: { enabled: false }
                                        },
                                        // series order: Users(Reg), FC(Reg), Users(Act), FC(Act)
                                        // Registered = strong colour; Active = lighter shade
                                        colors: ['#1677ff', '#28C76F', '#69b1ff', '#85d7a5'],
                                        // Registered series get a gradient fill so the area below
                                        // them is shaded; the gap down to the dashed Active line
                                        // becomes the visible "dormant users" region.
                                        fill: {
                                            type: ['gradient', 'gradient', 'solid', 'solid'],
                                            gradient: {
                                                shadeIntensity: 1,
                                                opacityFrom: 0.18,
                                                opacityTo: 0.0,
                                                stops: [0, 100]
                                            }
                                        },
                                        xaxis: {
                                            type: 'category',
                                            categories: cats,
                                            title: { text: 'Date' },
                                            labels: {
                                                rotate: -45,
                                                rotateAlways: true,
                                                style: { fontSize: '10px' }
                                            }
                                        },
                                        yaxis: {
                                            type: "logarithmic",
                                            title: { text: "User Count" },
                                        },
                                        stroke: {
                                            curve: "straight",
                                            dashArray: [0, 0, 5, 5],
                                            width: [2.5, 2.5, 1.5, 1.5]
                                        },
                                        markers: { size: 0 },
                                        legend: { show: true, position: 'top' },
                                        tooltip: {
                                            shared: true,
                                            intersect: false,
                                            x: { formatter: val => `${val}/${year}` },
                                        },
                                    }}
                                    series={transformedSeries}
                                    type="line"
                                    height={350}
                                />
                            );
                        })()
                    }
                    </div>

                </Card>

                {/* ----------------------------------------------------------------
                    P2 — Box plot: fact-checker response time (minutes) as 5-quantile
                    summary.  Each data point is one day's worth of response times.
                    Upper (green) = faster response; Lower (red) = slower response.
                ---------------------------------------------------------------- */}
                <Card title="[ Box Plot ] - Response Time ( as 5 quantile )"
                    variant="outlined"
                    style={{
                        border: '1px solid #dadce0'
                    }}
                >

                    <div style={{ minHeight: 280 }}>
                    {loading
                        ? <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="large" /></div>
                        : (() => {
                        const { year, month } = last_n_months[fetchedPointer];
                        const daysInMonth = new Date(year, month, 0).getDate();
                        // All dd/MM category labels for the selected month — drives even spacing
                        const cats = Array.from({ length: daysInMonth }, (_, i) =>
                            `${String(i + 1).padStart(2, '0')}/${String(month).padStart(2, '0')}`
                        );
                        // Map backend timestamp-keyed results to dd/MM strings
                        const lookup = {};
                        (P2?.results ?? []).forEach(pt => {
                            const d = new Date(pt.x);
                            const k = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
                            lookup[k] = pt.y;
                        });
                        // Inactive days use [0,0,0,0,0] — renders as a hairline at y=0,
                        // keeps all date labels visible and avoids NaN SVG whisker errors
                        const boxData = cats.map(c => ({ x: c, y: lookup[c] ?? [0, 0, 0, 0, 0] }));
                        return (
                            <ReactApexChart
                                options={{
                                    chart: {
                                        type: 'boxPlot',
                                        zoom: { enabled: false },
                                        toolbar: { show: false },
                                        animations: { enabled: false }
                                    },
                                    noData: {
                                        text: 'Awaiting data…',
                                        align: 'center',
                                        verticalAlign: 'middle',
                                        style: { color: '#ccc', fontSize: '13px' }
                                    },
                                    xaxis: {
                                        type: 'category',
                                        categories: cats,
                                        title: { text: 'Date' },
                                        labels: {
                                            rotate: -45,
                                            rotateAlways: true,
                                            style: { fontSize: '10px' }
                                        }
                                    },
                                    yaxis: {
                                        title: { text: 'Response Time (minutes)' }
                                    },
                                    plotOptions: {
                                        boxPlot: {
                                            colors: {
                                                upper: '#28C76F',
                                                lower: '#EA5455'
                                            }
                                        }
                                    },
                                    stroke: { width: 1 },
                                    grid: { show: true, strokeDashArray: 3 }
                                }}
                                series={[{ type: 'boxPlot', data: boxData }]}
                                type="boxPlot"
                                height={280}
                            />
                        );
                    })()}
                    </div>

                </Card>

                {/* ----------------------------------------------------------------
                    P3 — Heatmap: daily SPAM/FAKE/REAL/BLANK counts as a 4×31 grid.
                    Color scale: 0 = grey (#7A918D), -1 = white (#FFFFFF, invalid days),
                    positive values use label-specific colors (yellow/red/green/blue).
                    synthesize() provides the empty template when no data exists.
                ---------------------------------------------------------------- */}
                <Card title={
                    P3 && P3.total &&
                    `[ Heatmap ] - Blank vs Real vs Fake vs Spam | ${P3.total}`
                }
                    variant="outlined"
                    style={{
                        border: '1px solid #dadce0'
                    }}
                >

                    <div style={{ minHeight: 300 }}>
                    {loading
                        ? <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="large" /></div>
                        : P3 && P3.results &&
                        <ReactApexChart
                            options={{
                                chart: {
                                    type: 'heatmap',
                                    toolbar: { show: false }
                                },
                                legend: {
                                    show: false
                                },
                                dataLabels: {
                                    enabled: true
                                },
                                plotOptions: {
                                    heatmap: {
                                        colorScale: {
                                            inverse: false,
                                            ranges: [
                                                {
                                                    from: 0,
                                                    to: 0,
                                                    color: "#7A918D",
                                                    name: "EMPTY"
                                                },
                                                {
                                                    // y=-1 sentinel: days beyond the end of the month
                                                    // rendered white so the grid looks clean
                                                    from: -1,
                                                    to: -1,
                                                    color: "#FFFFFF",
                                                    name: "INVALID"
                                                }
                                            ]
                                        }
                                    }
                                },
                                // Series order (bottom→top): SPAM=yellow, FAKE=red, REAL=green, BLANK=blue
                                colors: ['#faad14', '#EA5455', '#28C76F', '#1677ff']
                            }}
                            series={P3.results}
                            type="heatmap"
                            height={300}
                        />
                    }
                    </div>

                </Card>

                {/* ----------------------------------------------------------------
                    Distribution chart type selector — applies to all four P4 cards.
                    Segmented maps display labels → internal chartType keys.

                    Laid out as a 3-column grid (not Flex) so the Segmented control
                    stays visually centered regardless of the two claim-total label
                    widths — Flex's space-between only guarantees the outer edges.

                    Claim totals switch source with the metric toggle: Users mode
                    reads P4_1's total_users_vs_{real,fake}_claims (users-first
                    wording); Posts mode reads P4_2's {real,fake}_claims_vs_total_users
                    (claims-first wording, matching each endpoint's own key order).
                ---------------------------------------------------------------- */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 8, padding: '0 16px' }}>
                    <div style={{ textAlign: 'left', fontSize: 12, color: '#4CAF50', fontWeight: 500 }}>
                        {metric === 'Users' && P4_1?.total_users_vs_real_claims &&
                            `${P4_1.total_users_vs_real_claims.users.toLocaleString()} Users vs ${P4_1.total_users_vs_real_claims.claims.toLocaleString()} Real Claims`}
                        {metric === 'Posts' && P4_2?.real_claims_vs_total_users &&
                            `${P4_2.real_claims_vs_total_users.claims.toLocaleString()} Real Claims vs ${P4_2.real_claims_vs_total_users.users.toLocaleString()} Users`}
                    </div>
                    <Flex align="center" gap={8} style={{ justifySelf: 'center' }}>
                        <span style={{ fontSize: 12, color: '#888' }}>View as</span>
                        <Segmented
                            options={['Histogram', 'Normal Dist.', 'Cumulative']}
                            value={{ histogram: 'Histogram', box_plot: 'Normal Dist.', percentile_curve: 'Cumulative' }[chartType]}
                            onChange={v => { const ct = { 'Histogram': 'histogram', 'Normal Dist.': 'box_plot', 'Cumulative': 'percentile_curve' }[v]; setChartType(ct); localStorage.setItem('fnma_home_charttype', ct); }}
                        />
                    </Flex>
                    <div style={{ textAlign: 'right', fontSize: 12, color: '#EA5455', fontWeight: 500 }}>
                        {metric === 'Users' && P4_1?.total_users_vs_fake_claims &&
                            `${P4_1.total_users_vs_fake_claims.users.toLocaleString()} Users vs ${P4_1.total_users_vs_fake_claims.claims.toLocaleString()} Fake Claims`}
                        {metric === 'Posts' && P4_2?.fake_claims_vs_total_users &&
                            `${P4_2.fake_claims_vs_total_users.claims.toLocaleString()} Fake Claims vs ${P4_2.fake_claims_vs_total_users.users.toLocaleString()} Users`}
                    </div>
                </div>

                {/* ----------------------------------------------------------------
                    P4 distribution cards — 2×2 grid (one per active/passive × real/fake).
                    Each card is rendered by renderDistChart() with the appropriate data,
                    colour, and title depending on the current metric (Users / Posts).

                    PIT (Probability Integral Transform) for Posts mode:
                    When metric='Posts', the user's raw score (a read-receipt %)
                    is mapped through the Users percentile_curve (P4_1) to find the
                    equivalent percentile rank on the Posts x-axis.  This allows the
                    "You are here" marker to be meaningful across both views.
                ---------------------------------------------------------------- */}
                <Row gutter={[10, 10]}>
                    {[
                        {
                            titleUsers: '[ viewed < 7 days ] - Active Users vs Real Claims',
                            titlePosts: '[ viewed < 7 days ] - Real Claims vs Active Users',
                            keyUsers: 'active vs real', keyPosts: 'real vs active', color: '#4CAF50', reverse: false
                        },
                        {
                            titleUsers: '[ viewed < 7 days ] - Active Users vs Fake Claims',
                            titlePosts: '[ viewed < 7 days ] - Fake Claims vs Active Users',
                            keyUsers: 'active vs fake', keyPosts: 'fake vs active', color: '#EA5455', reverse: true
                        },
                        {
                            titleUsers: '[ viewed > 7 days ] - Passive Users vs Real Claims',
                            titlePosts: '[ viewed > 7 days ] - Real Claims vs Passive Users',
                            keyUsers: 'passive vs real', keyPosts: 'real vs passive', color: '#4CAF50', reverse: false
                        },
                        {
                            titleUsers: '[ viewed > 7 days ] - Passive Users vs Fake Claims',
                            titlePosts: '[ viewed > 7 days ] - Fake Claims vs Passive Users',
                            keyUsers: 'passive vs fake', keyPosts: 'fake vs passive', color: '#EA5455', reverse: true
                        }
                    ].map(({ titleUsers, titlePosts, keyUsers, keyPosts, color, reverse }) => {
                        const isUsers = metric === 'Users';
                        const data = isUsers ? P4_1 : P4_2;
                        const key = isUsers ? keyUsers : keyPosts;
                        const title = isUsers ? titleUsers : titlePosts;
                        const series = data?.results?.[key];
                        // entity = what's being analysed; denominator = what it's measured against
                        const entity = isUsers ? 'users' : 'posts';
                        const denominator = isUsers ? 'posts' : 'users';

                        /**
                         * curveValueAtRank — forward quantile lookup (rank → score),
                         * trimmed to the backend's exact active range.
                         *
                         * rankLo/rankHi come directly from meta.min_xoi/max_xoi, which the
                         * backend computes by walking the FULL sorted per-entity score list
                         * (_compute_distribution_charts) to find exactly where a tied
                         * plateau at each edge ends — not an approximation from summary
                         * statistics (mean/std previously tried here, and before that a
                         * 21-point-curve tie scan) computed client-side. This mirrors
                         * ENGAGEMENT_REPORT_DESIGN.md's separation of concerns: precise
                         * computation happens once, where the full data lives; the browser
                         * only rescales and interpolates.
                         *
                         * @param {number}      rawScore — treated as a rank (0–100), rescaled
                         *                                 onto the [min_xoi, max_xoi] window.
                         * @param {object[]}    curve    — percentile_curve array { x: rank, y: score }.
                         *                                 NOT assumed rank-ascending: the backend
                         *                                 reverses array order (not point values)
                         *                                 for FAKE quadrants, so it's re-sorted here.
                         * @param {object|null} meta     — meta stats { min_xoi, max_xoi, ... }
                         *                                 for the same curve/quadrant.
                         * @returns {number|null}
                         */
                        const curveValueAtRank = (rawScore, curve, meta) => {
                            if (!curve || curve.length < 2) return null;
                            const pts = curve
                                .map(e => ({ rank: parseFloat(e.x), score: e.y }))
                                .sort((a, b) => a.rank - b.rank);

                            let rankLo = meta?.min_xoi ?? pts[0].rank;
                            let rankHi = meta?.max_xoi ?? pts[pts.length - 1].rank;
                            // Degenerate window — fall back to the full curve.
                            if (rankLo >= rankHi) { rankLo = pts[0].rank; rankHi = pts[pts.length - 1].rank; }

                            // Re-scale the 0–100% input onto the active rank window.
                            const scaledRank = rankLo + (rawScore / 100) * (rankHi - rankLo);

                            for (let i = 0; i < pts.length - 1; i++) {
                                if (scaledRank >= pts[i].rank && scaledRank <= pts[i + 1].rank) {
                                    const span = pts[i + 1].rank - pts[i].rank;
                                    if (span === 0) return pts[i].score;
                                    return pts[i].score + (scaledRank - pts[i].rank) / span * (pts[i + 1].score - pts[i].score);
                                }
                            }
                            if (scaledRank <= pts[0].rank) return pts[0].score;
                            return pts[pts.length - 1].score;
                        };

                        // Raw score from userDist (user's own read-receipt %, from user_dist)
                        const rawScore = usr.token && userDist ? (userDist.users?.[keyUsers]?.score ?? null) : null;
                        // In Users mode: use raw score directly.
                        // In Posts mode: treat the raw score as a rank and look up the
                        // corresponding value on the Posts distribution's own curve, so
                        // the marker always lands within that chart's observed range.
                        const userScore = rawScore != null
                            ? (isUsers ? rawScore : curveValueAtRank(rawScore, P4_2?.results?.[keyPosts]?.percentile_curve, P4_2?.results?.[keyPosts]?.meta))
                            : null;
                        return (
                            <Col xs={24} sm={12} key={key}>
                                <Card
                                    title={series?.stats ? `${title} ( ${series.stats} )` : title}
                                    extra={userScore != null ? <span style={{ color: '#1890ff', fontSize: 12, fontWeight: 600 }}>You {userScore.toFixed(2)}%</span> : null}
                                    variant="outlined"
                                    style={{ border: '1px solid #dadce0' }}
                                >
                                    <div style={{ minHeight: 230 }}>
                                    {loading
                                        ? <div style={{ height: 230, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="large" /></div>
                                        : renderDistChart(series, color, reverse, entity, denominator, userScore)
                                    }
                                    </div>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>

                {/* ----------------------------------------------------------------
                    Map section — Leaflet map with GeoJSON boundaries (B1) and
                    a heatmap layer (H1.1 for Users, H1.2 for Posts).
                    MapCleanup cancels RAF handles to prevent crash on fast navigation.
                    Center is set to Tamil Nadu (10.79°N, 77.50°E), zoom 7.
                    Alert banner below the map shows the data freshness timestamp
                    and the anonymised-analytics disclaimer as a scrolling marquee.
                ---------------------------------------------------------------- */}
                <Card styles={{ body: { padding: 0 } }} style={{ borderRadius: 8, overflow: "hidden" }}>
                    <MapContainer
                        style={{
                            borderRadius: 0,
                            overflow: "hidden",
                            zIndex: 50,
                        }}
                        center={{ lat: 10.7905, lng: 77.5047 }}
                        zoom={7}
                        scrollWheelZoom={true}>
                        <MapCleanup />
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {/* GeoJSON boundary layers — one per region in B1.
                            Each layer shows a blue outline; tooltip displays the property name. */}
                        {B1 && Object.entries(B1).map(([property, geojson]) => (
                            <GeoJSON
                                style={{
                                    color: "blue",
                                    fillColor: "transparent",
                                    weight: 1
                                }}
                                key={property}
                                data={geojson}
                            >
                                <Tooltip sticky>{property}</Tooltip>
                            </GeoJSON>
                        ))}

                        {/* Heatmap layer — H1.1 (Users) or H1.2 (Posts) based on metric toggle */}
                        {
                            metric === 'Users'
                                ?
                                <HeatmapLayer
                                    latlngs={H1_1.map((p) => [p[0], p[1], p[2]])}
                                />
                                :
                                <HeatmapLayer
                                    latlngs={H1_2.map((p) => [p[0], p[1], p[2]])}
                                />
                        }

                    </MapContainer>
                    {/* Marquee banner: data freshness timestamp + privacy disclaimer */}
                    <Alert
                        type='info'
                        banner
                        message={
                            <Marquee pauseOnHover gradient={false}>
                                [&nbsp;{ts}&nbsp;] &nbsp;·&nbsp; We collect anonymised location data to improve services through ethical and responsible analytics, without identifying or tracking individuals. &nbsp;·&nbsp; (&nbsp;© FNMA v1.0.2&nbsp;)
                            </Marquee>
                        }
                    />
                </Card>

            </Flex>
        </div>

    );
}
